import { useNavigate } from 'react-router'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { Bell, Loader2 } from 'lucide-react'
import { useNotifications, useUnreadCount } from '@/hooks/queries/useNotifications'
import { useMarkAsRead, useMarkAllAsRead } from '@/hooks/mutations/useNotifications'
import { Notification, NotificationType } from '@/interfaces/notification'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import 'dayjs/locale/vi'

// Configure dayjs
dayjs.extend(relativeTime)
dayjs.locale('vi')

const NotificationBell = () => {
  const navigate = useNavigate()
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useNotifications()
  const { data: unreadData } = useUnreadCount()
  const { mutate: markAsRead } = useMarkAsRead()
  const { mutate: markAllAsRead } = useMarkAllAsRead()

  const notifications = data?.pages.flatMap((page) => page.data) || []
  const unreadCount = unreadData?.count || 0

  const handleMarkAllAsRead = () => {
    markAllAsRead()
  }

  const handleNotificationClick = (notification: Notification) => {
    // Mark as read if not already
    if (!notification.isRead) {
      markAsRead({ notificationIds: [notification.id] })
    }

    // Navigate based on notification type
    switch (notification.type) {
      case NotificationType.JD_CONTRIBUTION:
      case NotificationType.JD_REACTION:
      case NotificationType.JD_TRENDING:
        // Navigate to the JD detail page
        if ('jobDescriptionId' in notification.metadata) {
          navigate(`/jd/${notification.metadata.jobDescriptionId}`)
        }
        break
      case NotificationType.JD_INVITATION:
        // Navigate to send JD page with pre-filled target user
        if ('targetUserId' in notification.metadata && 'surveyId' in notification.metadata) {
          navigate('/send-jd', {
            state: {
              targetUserId: notification.metadata.targetUserId,
              targetUserName: notification.metadata.targetUserName,
            },
          })
        }
        break
      default:
        break
    }
  }

  const formatTime = (dateString: string) => {
    try {
      return dayjs(dateString).fromNow()
    } catch {
      return dateString
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button className="outline-ring/50 active:scale-95 group bg-primary hover:scale-110 hover:shadow-xl relative p-3 transition-all duration-300 ease-out rounded-full shadow-md">
          <Bell className="w-5 h-5 text-white transition-colors duration-200" />
          {unreadCount > 0 && (
            <span className="-top-1 -right-1 border-primary text-primary absolute flex items-center justify-center w-5 h-5 text-xs font-bold bg-white border-2 rounded-full shadow-md">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-80 md:w-96 max-h-[500px] overflow-y-auto bg-white border-2 border-gray-200 shadow-2xl"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-900">Thông báo</h3>
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              className="hover:text-[#FEC8DC] text-xs font-semibold text-primary transition-colors"
            >
              Đánh dấu đã đọc
            </button>
          )}
        </div>

        {/* Notification List */}
        <div className="py-2">
          {isLoading ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="animate-spin text-primary w-6 h-6" />
              <span className="ml-2 text-sm text-gray-500">Đang tải...</span>
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-sm text-gray-500">Không có thông báo nào</p>
            </div>
          ) : (
            notifications.map((notification) => (
              <DropdownMenuItem
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                className={`p-4 cursor-pointer transition-colors ${
                  !notification.isRead
                    ? 'bg-purple-50 hover:bg-purple-100'
                    : 'bg-white hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start w-full gap-3">
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className="line-clamp-2 flex-1 mb-1 text-sm text-gray-900">
                        {notification.message}
                      </p>
                      {!notification.isRead && (
                        <div className="bg-primary flex-shrink-0 w-2 h-2 mt-1 rounded-full"></div>
                      )}
                    </div>
                    <p className="text-xs text-gray-400">{formatTime(notification.updatedAt)}</p>
                  </div>
                </div>
              </DropdownMenuItem>
            ))
          )}
        </div>

        {/* Footer */}
        {hasNextPage && (
          <div className="bg-gray-50 p-3 text-center border-t border-gray-200">
            <button
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              className="disabled:opacity-50 hover:text-[#FEC8DC] text-sm font-semibold text-primary transition-colors"
            >
              {isFetchingNextPage ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="animate-spin w-4 h-4" />
                  Đang tải...
                </span>
              ) : (
                'Xem thêm'
              )}
            </button>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default NotificationBell
